<template>
  <div id="fiscal-config-page">

    <!-- ================================================================
         Page volontairement en 3 ETAPES, dans l'ordre ou on les fait.
         La version precedente presentait deux tableaux a joindre mentalement
         et cinq cases « quel taux pour le groupe N ? ». C'est l'inverse de la
         question naturelle : l'utilisateur connait ses taux (il n'en a que
         trois), il ignore sous quel numero la caisse les imprime. On demande
         donc taux -> groupe, et jamais groupe -> taux.
         ================================================================ -->

    <!-- ETAT, en une phrase -->
    <vs-alert
      :color="ready ? 'success' : 'danger'"
      icon-pack="feather"
      :icon="ready ? 'icon-check-circle' : 'icon-alert-triangle'"
      class="mb-base"
    >
      <span v-if="ready" class="font-semibold">Ready — the register can print receipts.</span>
      <span v-else>
        <span class="font-semibold">Not ready.</span>
        {{ unmatched.length }} of your {{ rows.length }} VAT rate(s) still need a group number.
        Follow the two steps below.
      </span>
    </vs-alert>

    <!-- ================================================================
         STATUT DE LIAISON, en permanence.
         Le dashboard ne signale l'agent que s'il BLOQUE quelque chose (des
         bons en attente que personne ne collecte), pour ne pas crier tous les
         jours. Mais quand on vient ici, c'est justement pour savoir si la
         liaison est etablie : une page de configuration doit afficher l'etat
         en clair, tout le temps.
         ================================================================ -->
    <vx-card title="Connection status" class="mb-base">
      <div class="vx-row">

        <!-- 1. L'agent sur le poste du pilote -->
        <div class="vx-col md:w-1/2 w-full">
          <h6 class="mb-2">CasaMarcat agent</h6>
          <fiscal-agent-status :agent="agent" :pending="pending" :register="registerStatus" />
        </div>

        <!-- 2. La correspondance de TVA -->
        <div class="vx-col md:w-1/2 w-full">
          <div class="flex items-start">
            <feather-icon
              :icon="ready ? 'CheckCircleIcon' : 'XCircleIcon'"
              :svgClasses="'h-6 w-6 mr-3 ' + (ready ? 'text-success' : 'text-danger')"
            />
            <div>
              <h6 class="mb-1">VAT groups</h6>
              <p class="text-sm">
                <span v-if="ready"><b>Declared.</b> {{ rows.length }} rate(s) matched to a group.</span>
                <span v-else><b>Missing.</b> {{ unmatched.length }} of {{ rows.length }} rate(s)
                  still need a group number — see step 2 below.</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Les deux conditions sont independantes, et il faut les DEUX. On le
           dit explicitement : un agent qui tourne sans correspondance de TVA
           ne peut rien imprimer, et l'inverse aussi. -->
      <vs-alert :color="ready && agent && !agent.stale ? 'success' : 'primary'" class="mt-4">
        <span v-if="ready && agent && !agent.stale">
          Everything is in place — a cash invoice can be printed on the register.
        </span>
        <span v-else>
          Both are needed: the agent must be running <b>and</b> the VAT groups declared. Receipts
          queued while one of them is missing are not lost — they wait.
        </span>
      </vs-alert>

      <div class="mt-4">
        <vs-button color="primary" type="border" icon-pack="feather" icon="icon-refresh-cw" @click="loadVatGroups">
          Refresh
        </vs-button>
      </div>
    </vx-card>

    <!-- ================================================================
         RAPPORT Z. L'ERP ne transmet rien a l'ANAF : la caisse envoie elle-
         meme chaque Z, signe par sa memoire fiscale. On le dit en clair pour
         que personne ne cherche un « envoi ANAF » dans l'ERP. Notre part :
         declencher la cloture chaque jour de vente.
         ================================================================ -->
    <vx-card title="Daily Z report" class="mb-base">
      <p class="mb-3">
        The Z report closes the fiscal day. <b>The register sends it to ANAF by itself</b>, over its
        own internet connection — the ERP only triggers it. This happens automatically every evening
        at <b>23:30 (Bucharest time)</b> when fiscal receipts were issued since the last Z.
      </p>

      <vs-alert v-if="z && z.overdue" color="danger" class="mb-3">
        <b>Z report missing.</b> Sales since {{ fmtDay(z.oldest_unclosed_at) }} have not been closed.
        Check that the register is on and in mode 6, then print the Z report below.
      </vs-alert>
      <vs-alert v-if="z && z.unknown_job_id" color="warning" class="mb-3">
        <b>The last Z report (job #{{ z.unknown_job_id }}) has no confirmed result</b> — it may have printed.
        Look at the register's paper, then mark it on the
        <router-link to="/fiscal-jobs">Casa de Marcat page</router-link>.
      </vs-alert>

      <div class="text-sm mb-4">
        <div>
          <span class="text-grey">Last Z report:</span>
          <b v-if="z && z.last_at">{{ fmtDateTime(z.last_at) }}<span v-if="z.last_no"> (n° {{ z.last_no }})</span></b>
          <span v-else>none issued from the ERP yet</span>
        </div>
        <div v-if="z">
          <span class="text-grey">Receipts since:</span> {{ z.receipts_since }}
          <span v-if="z.live_job_id" class="ml-2 text-warning">— a Z report is queued (job #{{ z.live_job_id }})</span>
        </div>
      </div>

      <vs-button
        color="danger"
        type="border"
        icon-pack="feather"
        icon="icon-printer"
        :disabled="printingZ || !agentReady || !!(z && z.live_job_id)"
        @click="openZConfirm"
      >
        {{ printingZ ? 'Sending…' : 'Print Z report now' }}
      </vs-button>
      <span v-if="!agentReady" class="ml-3 text-sm text-danger">
        The agent must be online for this — see Connection status above.
      </span>

      <vs-alert v-if="zResult" :color="zResult.color" class="mt-4">{{ zResult.text }}</vs-alert>

      <!-- HISTORIQUE. Ce que l'ERP a DECLENCHE, et ce que la caisse a repondu.
           La transmission ANAF qui suit est faite par la caisse : elle n'est
           pas visible d'ici, et l'ecran ne pretend pas le contraire. -->
      <h6 class="mt-6 mb-2">History</h6>
      <p class="text-sm text-grey mb-3">
        Z reports triggered by the ERP and what the register answered. The transmission to ANAF
        that follows is done by the register and is not visible from here.
      </p>
      <div v-if="!zHistory.length" class="text-sm text-grey">No Z report triggered from the ERP yet.</div>
      <div v-else class="z-history">
        <table class="w-full text-sm">
          <thead>
            <tr>
              <th>Date</th>
              <th>Triggered by</th>
              <th>Result</th>
              <th>Z n°</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="h in zHistory" :key="h.id">
              <td>{{ fmtDateTime(h.created_at) }}</td>
              <td>
                <span v-if="h.trigger === 'automatic'">Automatic (23:30)</span>
                <span v-else>{{ h.by }}</span>
              </td>
              <td>
                <vs-chip :color="zStatus(h).color" class="m-0">{{ zStatus(h).label }}</vs-chip>
              </td>
              <td>{{ h.nrz || h.receipt_no || '—' }}</td>
              <td class="text-grey">
                <span v-if="h.err_code === '-33022'">Register not connected (not in mode 6 or switched off) — nothing printed.</span>
                <span v-else-if="h.err_info">{{ h.err_info }}</span>
                <span v-if="h.attempts > 1"> · {{ h.attempts }} attempts</span>
                <span v-if="h.resolution_note"> · {{ h.resolution_note }}</span>
                <router-link v-if="h.can_resolve" to="/fiscal-jobs" class="ml-1">Check and mark it</router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </vx-card>

    <!-- Confirmation du Z manuel : il cloture la journee fiscale et ne
         s'annule pas. -->
    <vs-popup :active.sync="zConfirmOpen" :title="zForceReason ? 'Print another Z report?' : 'Print the Z report'">
      <vs-alert :color="zForceReason ? 'warning' : 'danger'" class="mb-4">
        <span v-if="zForceReason">{{ zForceReason }} Print another Z report anyway?</span>
        <span v-else>
          This closes the fiscal day on the register and <b>cannot be undone</b>. The register then
          sends it to ANAF by itself.
        </span>
      </vs-alert>
      <div class="flex justify-end mt-4">
        <vs-button color="dark" type="flat" class="mr-3" @click="closeZConfirm">Cancel</vs-button>
        <vs-button color="danger" :disabled="printingZ" @click="submitZ">
          {{ printingZ ? 'Sending…' : (zForceReason ? 'Print another Z report' : 'Print Z report') }}
        </vs-button>
      </div>
    </vs-popup>

    <!-- ============================ ETAPE 1 ============================ -->
    <vx-card class="mb-base">
      <h4 class="mb-4">Step 1 — Print an X report on the register</h4>

      <p class="mb-4">
        The register prints VAT under <b>group numbers</b> (1 to 5), not under percentages. Which
        number carries which rate was set inside the device when it was installed, and there is no
        way to ask it over the cable — it has to be read off a printed report.
      </p>

      <vs-alert color="success" class="mb-4">
        An <b>X report</b> is safe: it is a read-only report. It records no sale, no turnover, and
        changes nothing.
      </vs-alert>

      <!-- Bouton direct : la commande part dans la file et l'agent la prend.
           Avant, il fallait telecharger un fichier puis aller le deposer a la
           main sur le poste du pilote — pour une commande qu'on relance
           chaque fois qu'on doute d'un reglage. -->
      <vs-button
        color="success"
        icon-pack="feather"
        icon="icon-printer"
        :disabled="printingX || !agentReady"
        @click="printReportX"
      >
        {{ printingX ? 'Sending…' : 'Print X report now' }}
      </vs-button>

      <span v-if="!agentReady" class="ml-3 text-sm text-danger">
        The agent must be online for this — see Connection status above.
      </span>
      <span v-else class="ml-3 text-sm text-grey">
        The register prints within a few seconds.
      </span>

      <vs-alert v-if="xResult" :color="xResult.color" class="mt-4">{{ xResult.text }}</vs-alert>

      <div class="mt-4 text-sm text-grey">
        Agent not running? Download the command file and drop it in the driver's
        <code>\Bonuri</code> folder by hand — same result.
        <a href="#" class="underline ml-1" @click.prevent="downloadReportX">Download raportX.txt</a>
        <br />
        Or, on the driver host: <code>node bin/casamarcat.js reportx</code>
      </div>
    </vx-card>

    <!-- ============================ ETAPE 2 ============================ -->
    <vx-card class="mb-base">
      <h4 class="mb-2">Step 2 — Match each rate to its group number</h4>
      <p class="mb-4">
        Your cash sales use the rates below. On the X report you will see lines per VAT group —
        something like <code>GRUPA 1 … 21%</code> or <code>TVA B 11%</code>. Pick the matching
        number for each rate.
      </p>

      <vs-table :data="rows" noDataText="No cash invoice found in the last 12 months">
        <template slot="thead">
          <vs-th>VAT rate</vs-th>
          <vs-th>Used on</vs-th>
          <vs-th>Group number on the register</vs-th>
        </template>
        <template slot-scope="{ data }">
          <vs-tr v-for="(r, i) in data" :key="i" :data="r">
            <vs-td><span class="text-lg font-semibold">{{ r.rate }} %</span></vs-td>
            <vs-td>
              <span v-if="r.invoice_count">{{ r.invoice_count }} cash invoice(s)</span>
              <span v-else class="text-sm text-grey">declared, not used recently</span>
            </vs-td>
            <vs-td>
              <vs-select v-model="choice[r.rate]" class="w-full" style="max-width:220px" autocomplete>
                <vs-select-item :value="null" text="— not set —" />
                <vs-select-item v-for="n in [1,2,3,4,5]" :key="n" :value="n" :text="'Group ' + n" />
              </vs-select>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>

      <vs-alert v-if="draftError" color="danger" class="mt-4">{{ draftError }}</vs-alert>

      <div class="mt-4">
        <vs-button color="primary" :disabled="savingVat" @click="saveVatGroups">Save</vs-button>
        <vs-button color="dark" type="flat" class="ml-3" @click="loadVatGroups">Cancel</vs-button>
      </div>

      <!-- Repli honnete : si le rapport X n'est pas lisible, la bonne reponse
           est d'appeler l'installateur, pas de deviner. Un groupe faux imprime
           une TVA legalement fausse et la caisse repond quand meme « OK ». -->
      <vs-alert color="warning" class="mt-6">
        <span class="font-semibold">Can't tell which group is which?</span>
        Ask whoever installed the register (the X report footer usually names the distributor).
        Do not guess: a wrong group prints a legally wrong VAT on a receipt that cannot be
        cancelled, and the driver still reports success.
      </vs-alert>
    </vx-card>

    <!-- ============================ ETAPE 3 ============================ -->
    <vx-card class="mb-base">
      <h4 class="mb-2">Step 3 — Try a receipt</h4>
      <p class="mb-4">
        Optional check. Fill in one line, download the file, and drop it on the register through
        the agent: <code>node bin/casamarcat.js send &lt;file&gt;</code>.
      </p>

      <div class="vx-row">
        <div class="vx-col md:w-1/2 w-full">
          <vs-input label="Item label" v-model="test.name" class="w-full" />
          <vs-input label="Price incl. VAT (RON)" v-model="test.price" class="w-full mt-4" />

          <label class="vs-input--label mt-4 block">VAT rate</label>
          <vs-select v-model="test.rate" class="w-full">
            <vs-select-item
              v-for="r in mappedRows"
              :key="r.rate"
              :value="r.rate"
              :text="r.rate + ' % (group ' + choice[r.rate] + ')'"
            />
          </vs-select>
          <span v-if="!mappedRows.length" class="text-sm text-danger">
            Finish step 2 first — there is deliberately no default group.
          </span>

          <label class="vs-input--label mt-4 block">Paid by</label>
          <vs-select v-model="test.paymentType" class="w-full">
            <vs-select-item :value="1" text="Cash" />
            <vs-select-item :value="2" text="Card" />
          </vs-select>

          <label class="vs-input--label mt-4 block">Receipt type</label>
          <vs-select v-model="test.kind" class="w-full">
            <vs-select-item value="nonfiscal" text="Non-fiscal — safe, records nothing" />
            <vs-select-item value="fiscal" text="Fiscal — REAL, cannot be cancelled" />
          </vs-select>
        </div>

        <div class="vx-col md:w-1/2 w-full">
          <label class="vs-input--label block">File that will be sent — <code>{{ fileName }}</code></label>
          <pre class="p-3 text-xs bg-grey-light rounded overflow-x-auto" style="min-height:140px">{{ preview }}</pre>
          <div class="mt-2 text-sm">Total: <b>{{ total.toFixed(2) }} RON</b></div>

          <vs-alert v-if="buildErrors.length" color="danger" class="mt-4">
            <ul class="text-sm"><li v-for="(e, i) in buildErrors" :key="i">{{ e }}</li></ul>
          </vs-alert>
        </div>
      </div>

      <!-- Le defaut est « non fiscal » et l'avertissement n'apparait que si on
           choisit l'autre : un essai ne doit pas pouvoir devenir un vrai bon
           par inattention. -->
      <vs-alert v-if="test.kind === 'fiscal'" color="danger" icon-pack="feather" icon="icon-alert-octagon" class="mt-4">
        <span class="font-semibold">This will be a real fiscal receipt.</span>
        It enters the register's electronic journal and its Z report, with its turnover, and cannot
        be cancelled. For a harmless check, pick “Non-fiscal” above.
      </vs-alert>

      <div class="mt-4">
        <vs-button color="primary" :disabled="!!buildErrors.length" @click="downloadReceipt">
          Download {{ fileName }}
        </vs-button>
      </div>
    </vx-card>

    <!-- ===================== DETAILS, replies ========================== -->
    <vx-card title="Details" class="mb-base" collapse-action :is-collapsed="true">
      <p class="mb-4 text-sm">
        The driver is controlled <b>by files</b>: commands go to <code>...\Bonuri\&lt;id&gt;.txt</code> and
        the answer appears in <code>...\Raspuns\&lt;id&gt;.txt</code>. A browser cannot write files, so
        the <code>XYLO-X/CasaMarcat</code> agent does it on the driver host. These paths are a
        reminder only — the agent reads its own configuration.
      </p>

      <div class="vx-row">
        <div class="vx-col md:w-1/2 w-full">
          <vs-input label="Commands folder" v-model="cfg.bonuri" class="w-full" placeholder="C:\FiscalNet\Bonuri" />
        </div>
        <div class="vx-col md:w-1/2 w-full">
          <vs-input label="Answers folder" v-model="cfg.raspuns" class="w-full" placeholder="C:\FiscalNet\Raspuns" />
        </div>
      </div>
      <vs-button color="primary" type="border" class="mt-4" @click="saveLocal">Remember locally</vs-button>

      <h6 class="mt-6 mb-2">Read an answer from the driver</h6>
      <vs-textarea v-model="responseRaw" rows="3" class="w-full" placeholder="BONOK=1&#10;NRBON=42" />
      <vs-alert v-if="responseRaw.trim()" :color="responseColor" class="mt-2">
        <div class="font-semibold mb-1">{{ responseTitle }}</div>
        <div class="text-sm">{{ responseText }}</div>
      </vs-alert>
    </vx-card>

  </div>
</template>

<script>
import axios from '@/axios.js'
import FiscalAgentStatus from '@/components/FiscalAgentStatus.vue'
import {
  buildReceipt,
  receiptTotal,
  receiptToText,
  receiptFileName,
  interpretResponse,
  reportXLine,
  loadConfig,
  saveConfig,
} from '@/helpers/fiscalnet.js'

export default {
  components: { FiscalAgentStatus },
  data() {
    return {
      cfg: loadConfig(),
      responseRaw: '',
      savingVat: false,
      draftError: '',
      vat: { groups: [], usage: [], used_rates: [], missing_rates: [], complete: false },
      agent: null,
      pending: 0,
      printingX: false,
      xResult: null,
      // Rapport Z : état lu dans /fiscal/counts (clé `z`).
      z: null,
      printingZ: false,
      zResult: null,
      zHistory: [],
      // État de la caisse (IP, joignable, mode 6), calculé par le serveur.
      registerStatus: null,
      // Fenêtre de confirmation du Z manuel ; `zForceReason` = seconde
      // confirmation demandée par le serveur (409 can_force).
      zConfirmOpen: false,
      zForceReason: '',
      // taux -> numéro de groupe. C'est le sens dans lequel l'utilisateur
      // raisonne : il connaît ses taux, il cherche leur numéro sur la caisse.
      choice: {},
      test: {
        name: 'ARTICOL TEST',
        price: '1.00',
        rate: null,
        paymentType: 1,
        // Défaut NON fiscal : un essai ne doit pas devenir un vrai bon par
        // inattention.
        kind: 'nonfiscal',
      },
    }
  },
  computed: {
    /** Lignes de l'étape 2 : union des taux utilisés et des taux déjà déclarés. */
    rows() {
      const map = new Map()
      for (const u of this.vat.usage || []) {
        map.set(Number(u.tva_pct), { rate: Number(u.tva_pct), invoice_count: Number(u.invoice_count) })
      }
      for (const g of this.vat.groups || []) {
        const r = Number(g.tva_pct)
        if (!map.has(r)) map.set(r, { rate: r, invoice_count: 0 })
      }
      // Par volume décroissant : le taux qui porte les encaissements d'abord.
      return [...map.values()].sort((a, b) => b.invoice_count - a.invoice_count || a.rate - b.rate)
    },
    unmatched() {
      return this.rows.filter((r) => !this.choice[r.rate])
    },
    /** Inutile de proposer le bouton si personne ne peut prendre la commande. */
    agentReady() {
      return !!this.agent && !this.agent.stale
    },
    mappedRows() {
      return this.rows.filter((r) => !!this.choice[r.rate])
    },
    ready() {
      return this.rows.length > 0 && this.unmatched.length === 0 && this.vat.groups.length > 0
    },

    items() {
      return [{
        name: this.test.name,
        price: this.test.price,
        qty: 1,
        um: 'buc',
        // Pas de défaut : un groupe deviné imprimerait une TVA légalement
        // fausse, et le pilote répondrait quand même BONOK=1.
        grtva: this.test.rate === null ? null : this.choice[this.test.rate] || null,
        grdep: 1,
      }]
    },
    built() {
      return buildReceipt({
        items: this.items,
        payments: [{ type: this.test.paymentType, amount: receiptTotal(this.items) }],
      })
    },
    buildErrors() { return this.built.errors },
    preview() { return this.built.lines.join('\n') },
    total() { return receiptTotal(this.items) },
    fileName() {
      try {
        return receiptFileName({ id: 'test-1', kind: this.test.kind })
      } catch (e) {
        return '(id required)'
      }
    },

    response() { return interpretResponse(this.responseRaw) },
    responseColor() {
      if (this.response.ok === true) return 'success'
      if (this.response.ok === false) return 'danger'
      // ok === null : indéterminé. Jamais « danger » — l'utilisateur rejouerait.
      return 'warning'
    },
    responseTitle() {
      const r = this.response
      if (r.ok === true) return `Receipt issued${r.receiptNo ? ` — n° ${r.receiptNo}` : ''}`
      if (r.ok === false) return `Receipt refused${r.errCode ? ` (${r.errCode})` : ''}`
      return 'Indeterminate — do not retry'
    },
    responseText() {
      const r = this.response
      if (r.ok === true) return 'The driver confirms the receipt was printed.'
      if (r.ok === false) return r.errInfo || 'The driver refused it, with no further detail.'
      return 'The receipt may or may not have printed. Check the paper and the register’s journal '
        + 'before doing anything else — reprinting could charge the customer twice.'
    },
  },
  created() {
    this.loadVatGroups()
  },
  methods: {
    loadVatGroups() {
      this.draftError = ''
      // Deux appels : la correspondance de TVA et l'etat de l'agent. Ce dernier
      // vient de /counts, qui porte deja le battement.
      axios.get('/fiscal/counts')
        .then((res) => {
          this.agent = (res.data && res.data.agent) || null
          this.pending = (res.data && res.data.pending) || 0
          this.z = (res.data && res.data.z) || null
          this.registerStatus = (res.data && res.data.register) || null
        })
        .catch(() => { this.agent = null })

      this.loadZHistory()

      axios.get('/fiscal/vat-groups')
        .then((res) => {
          this.vat = res.data
          const c = {}
          for (const g of this.vat.groups || []) c[Number(g.tva_pct)] = Number(g.grtva)
          this.choice = c
          if (this.test.rate === null && this.mappedRows.length) {
            this.test.rate = this.mappedRows[0].rate
          }
        })
        .catch((err) => {
          this.$vs.notify({
            color: 'danger',
            title: 'Could not load the configuration',
            text: (err.response && err.response.data && err.response.data.error) || err.message,
          })
        })
    },

    saveVatGroups() {
      this.draftError = ''

      const groups = []
      const seenSlot = new Map()

      for (const r of this.rows) {
        const slot = this.choice[r.rate]
        if (!slot) continue

        // Deux taux sur le même numéro : la caisse ne saurait pas lequel
        // appliquer, et deux bons du même produit pourraient différer.
        if (seenSlot.has(slot)) {
          this.draftError = `Group ${slot} is used for both ${seenSlot.get(slot)} % and ${r.rate} %. `
            + 'Each group carries one rate.'
          return
        }
        seenSlot.set(slot, r.rate)
        groups.push({ grtva: Number(slot), tva_pct: Number(r.rate) })
      }

      if (!groups.length) {
        this.draftError = 'Nothing to save — set at least one group number.'
        return
      }

      this.savingVat = true
      axios.put('/fiscal/vat-groups', { groups })
        .then(() => {
          this.$vs.notify({
            color: 'success',
            title: 'Saved',
            text: `${groups.length} rate(s) matched to a group.`,
          })
          this.loadVatGroups()
        })
        .catch((err) => {
          this.$vs.notify({
            color: 'danger',
            title: 'Could not save',
            text: (err.response && err.response.data && err.response.data.error) || err.message,
          })
        })
        .finally(() => { this.savingVat = false })
    },

    saveLocal() {
      saveConfig(this.cfg)
      this.$vs.notify({ color: 'success', title: 'Saved in this browser', text: this.cfg.bonuri })
    },

    download(name, text) {
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = name
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(a.href)
    },

    printReportX() {
      this.printingX = true
      this.xResult = null
      axios.post('/fiscal/service-command', { command: 'reportx' })
        .then((res) => {
          this.xResult = {
            color: 'success',
            text: `Queued (job #${res.data.job_id}). The register should print within a few seconds.`,
          }
        })
        .catch((err) => {
          const data = (err.response && err.response.data) || {}
          this.xResult = { color: 'danger', text: data.error || err.message }
        })
        .finally(() => { this.printingX = false })
    },

    loadZHistory() {
      axios.get('/fiscal/z-history')
        .then((res) => { this.zHistory = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.zHistory = [] })
    },

    /** Libellé et couleur d'une ligne d'historique. */
    zStatus(h) {
      return {
        queued: { label: 'Waiting', color: 'warning' },
        leased: { label: 'Printing…', color: 'warning' },
        writing: { label: 'Printing…', color: 'warning' },
        printed: { label: 'Printed', color: 'success' },
        resolved_printed: { label: 'Printed (checked)', color: 'success' },
        unknown: { label: 'To verify', color: 'warning' },
        failed: { label: 'Failed', color: 'danger' },
        rejected: { label: 'Failed', color: 'danger' },
        resolved_not_printed: { label: 'Not printed (checked)', color: 'danger' },
      }[h.status] || { label: h.status, color: 'dark' }
    },

    openZConfirm() {
      this.zForceReason = ''
      this.zResult = null
      this.zConfirmOpen = true
    },

    closeZConfirm() {
      this.zConfirmOpen = false
      this.zForceReason = ''
    },

    /**
     * Z manuel. Le serveur vérifie les gardes. Une seconde
     * confirmation n'apparaît que s'il répond 409 avec `can_force` (Z déjà fait
     * aujourd'hui, ou dernier Z indéterminé) — c'est lui qui décide ce qui est
     * forçable, pas cet écran.
     */
    submitZ() {
      if (this.printingZ) return
      const force = !!this.zForceReason
      this.printingZ = true
      axios.post('/fiscal/report-z', { force })
        .then((res) => {
          this.closeZConfirm()
          this.zResult = {
            color: 'success',
            text: `Queued (job #${res.data.job_id}). The register should print the Z report within a few seconds.`,
          }
        })
        .catch((err) => {
          const status = err.response && err.response.status
          const data = (err.response && err.response.data) || {}
          if (status === 409 && data.can_force && !force) {
            // Même fenêtre : on demande seulement la confirmation.
            this.zForceReason = data.error
            return
          }
          this.closeZConfirm()
          this.zResult = { color: 'danger', text: data.error || err.message }
        })
        .finally(() => {
          this.printingZ = false
          this.loadVatGroups()
        })
    },

    fmtDay(value) {
      const d = value ? new Date(value) : null
      if (!d || Number.isNaN(d.getTime())) return '?'
      const pad = (n) => String(n).padStart(2, '0')
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`
    },
    fmtDateTime(value) {
      const d = value ? new Date(value) : null
      if (!d || Number.isNaN(d.getTime())) return '?'
      const pad = (n) => String(n).padStart(2, '0')
      return `${this.fmtDay(value)} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },

    downloadReceipt() { this.download(this.fileName, receiptToText(this.built.lines)) },
    downloadReportX() { this.download('raportX.txt', receiptToText([reportXLine()])) },
  },
}
</script>

<style scoped>
.z-history { overflow-x: auto; }
.z-history table { border-collapse: collapse; }
.z-history th {
  text-align: left; font-weight: 600; font-size: .75rem; text-transform: uppercase;
  letter-spacing: .04em; opacity: .7; padding: .4rem .6rem; border-bottom: 1px solid rgba(0,0,0,.1);
}
.z-history td { padding: .45rem .6rem; border-bottom: 1px solid rgba(0,0,0,.05); vertical-align: middle; }
</style>
